import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import { MetierSchema, type MetierRow, type MetierUpdate } from "@/schemas/metier";
import { MOCK_METIERS } from "@/data/mock";
import { z } from "zod";

type MetierInsert = {
  id: string;
  nom: string;
  couleur?: string;
  actif?: boolean;
};

function fromMocks(): MetierRow[] {
  return MOCK_METIERS as unknown as MetierRow[];
}

async function listFromSupabase(role: string): Promise<MetierRow[]> {
  // metiers sont en lecture publique (RLS authenticated-read) : on peut soit
  // utiliser le RPC, soit un .from() direct. On garde le RPC par cohérence.
  const { data, error } = await supabase.rpc("get_metiers_for_role", { p_role: role });
  if (error) throw new Error(`listMetiers: ${error.message}`);
  return z.array(MetierSchema).parse(data ?? []);
}

async function updateOnSupabase(role: string, id: string, patch: MetierUpdate): Promise<MetierRow> {
  const { data, error } = await supabase.rpc("update_entity_with_perms", {
    p_table_name: "metiers",
    p_role: role,
    p_id: id,
    p_data: patch,
  });
  if (error) throw new Error(`updateMetier(${id}): ${error.message}`);
  return MetierSchema.parse(data);
}

export const metiersRepository = {
  async list(role: string): Promise<MetierRow[]> {
    if (!USE_SUPABASE) return fromMocks();
    return listFromSupabase(role);
  },

  async update(role: string, id: string, patch: MetierUpdate): Promise<MetierRow> {
    if (!USE_SUPABASE) {
      const m = fromMocks().find((x) => x.id === id);
      if (!m) throw new Error(`Metier introuvable: ${id}`);
      return { ...m, ...patch };
    }
    return updateOnSupabase(role, id, patch);
  },

  async create(data: MetierInsert): Promise<MetierRow> {
    if (!USE_SUPABASE) {
      return { id: data.id, nom: data.nom, couleur: data.couleur ?? "", actif: data.actif ?? true };
    }
    // Admin only (RLS enforces). Direct INSERT via table.
    const { data: row, error } = await supabase.from("metiers").insert(data).select().single();
    if (error) throw new Error(`createMetier: ${error.message}`);
    return MetierSchema.parse(row);
  },

  async remove(id: string): Promise<void> {
    if (!USE_SUPABASE) return;
    const { error } = await supabase.from("metiers").delete().eq("id", id);
    if (error) throw new Error(`deleteMetier(${id}): ${error.message}`);
  },
};
